import React, { useState, useEffect, useCallback, useRef } from "react";
import { getSubscribedMembers, skipMealSubscribedMember, reduceDailyMeals, deliverMealSubscribedMember, setMealGivenSubscribedMember, pusher } from "../../services/api";
import toast from "react-hot-toast";
import MealInfoModal from "../Modals/MealInfoModal";
import { timeToMinutes } from "../../utils/timeUtils";
import { WEEK_DAYS, getWeekDates, dayState, shortDate, toDateStr } from "../../utils/weekUtils";

const DIETS = ["Veg", "Non veg", "Eggs", "Fish"];
const BASES = ["Rice", "Salad", "Keto", "Only Protein", "Pasta", "Sandwich"];

const DIET_CARD = {
    "Veg": { box: "bg-green-50 border-green-100", head: "text-green-800 border-green-200", row: "text-green-700", chip: "text-green-600 bg-green-200" },
    "Non veg": { box: "bg-red-50 border-red-100", head: "text-red-800 border-red-200", row: "text-red-700", chip: "text-red-600 bg-red-200" },
    "Eggs": { box: "bg-yellow-50 border-yellow-100", head: "text-yellow-800 border-yellow-200", row: "text-yellow-700", chip: "text-yellow-600 bg-yellow-200" },
    "Fish": { box: "bg-indigo-50 border-indigo-100", head: "text-indigo-800 border-indigo-200", row: "text-indigo-700", chip: "text-indigo-600 bg-indigo-200" },
};

const PREF_COLORS = {
    "Veg": "bg-green-100 text-green-800",
    "Non veg": "bg-red-100 text-red-800",
    "Eggs": "bg-yellow-100 text-yellow-800",
    "Fish": "bg-indigo-100 text-indigo-800",
    "Rice": "bg-amber-100 text-amber-800",
    "Rice bowls": "bg-amber-100 text-amber-800",
    "Salad": "bg-lime-100 text-lime-800",
    "Keto": "bg-purple-100 text-purple-800",
    "Only Protein": "bg-pink-100 text-pink-800",
    "Pasta": "bg-orange-100 text-orange-800",
    "Sandwich": "bg-stone-200 text-stone-800",
};

// Three 2-hour slots, shared between lunch and dinner:
// 11-1 / 5-7, 1-3 / 7-9, 3-5 / 9-11. Anything outside 11:00–23:00 is gray.
const SLOT_COLORS = ["bg-amber-100 text-amber-900 border-amber-200", "bg-sky-100 text-sky-900 border-sky-200", "bg-violet-100 text-violet-900 border-violet-200"];
const NO_SLOT = "bg-gray-100 text-gray-600 border-gray-200";
// Meals left at or below this = plan ending, remind admin to ask for renewal
const LOW_MEALS = 4;
const mealsLeft = (n) => parseInt(String(n ?? "").replace(/[^0-9]/g, ""), 10);

const slotClass = (t) => {
    const mins = timeToMinutes(t);
    if (mins < 11 * 60 || mins >= 23 * 60) return NO_SLOT;
    return SLOT_COLORS[Math.floor((mins - 11 * 60) / 120) % 3];
};

const TodaysMealManagement = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState([]);
    const [modalTitle, setModalTitle] = useState("");
    const [mealGivenDrafts, setMealGivenDrafts] = useState({});
    const [blinkKeys, setBlinkKeys] = useState({});
    const [alarm, setAlarm] = useState(null);
    const audioCtxRef = useRef(null);

    const currentDayIndex = new Date().getDay();
    const todayName = currentDayIndex === 0
        ? "Sunday"
        : ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][currentDayIndex];

    const [selectedDay, setSelectedDay] = useState(todayName);
    const [selectedPeriod, setSelectedPeriod] = useState("Lunch");

    const fetchMembers = useCallback(async (showSpinner = true) => {
        try {
            if (showSpinner) setLoading(true);
            const res = await getSubscribedMembers();
            if (res.data && res.data.success) {
                setMembers(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching members:", err);
            toast.error("Failed to fetch members");
        } finally {
            if (showSpinner) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    const playTone = useCallback((freq, duration) => {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
        const ctx = audioCtxRef.current;
        ctx.resume?.();

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        gain.gain.value = 0.15;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + duration);
    }, []);

    const blink = useCallback((key) => {
        setBlinkKeys(prev => ({ ...prev, [key]: true }));
        setTimeout(() => setBlinkKeys(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
        }), 10000);
    }, []);

    // Live updates: anything either side changes lands here without a reload
    useEffect(() => {
        const channel = pusher.subscribe("subscriptions-channel");

        channel.bind("subscription_updated", () => fetchMembers(false));

        channel.bind("meal_skipped", (data) => {
            fetchMembers(false);
            if (data?.date !== toDateStr()) return; // future days just refresh quietly

            const who = data?.name || "A member";
            const undo = data?.action !== "skip";

            blink(`${data.memberId}-${data.period}-${data.date}`);
            setAlarm({ text: `${who} ${undo ? "un-skipped" : "skipped"} ${data.period} on ${data.day}`, freq: 880 });
            toast(`${who} ${undo ? "un-skipped" : "skipped"} ${data.period}`, { icon: "⚠️" });
        });

        channel.bind("meal_customized", (data) => {
            fetchMembers(false);
            const todayChanges = (data?.changes || []).filter(c => c.date === toDateStr());
            if (todayChanges.length === 0) return;

            const who = data?.name || "A member";
            todayChanges.forEach(c => blink(`${data.memberId}-${c.period}-${c.date}`));
            setAlarm({ text: `${who} changed today's meal preferences`, freq: 1400 });
            toast(`${who} changed their meal preferences`, { icon: "🔔" });
        });

        return () => {
            channel.unbind_all();
            pusher.unsubscribe("subscriptions-channel");
        };
    }, [fetchMembers, blink]);

    // Skips and customer modifications both beep until the admin acknowledges them
    useEffect(() => {
        if (!alarm) return;
        const ring = () => playTone(alarm.freq, 0.25);
        ring();
        const id = setInterval(ring, 1200);
        return () => clearInterval(id);
    }, [alarm, playTone]);

    const handleDayToggle = (day) => {
        setSelectedDay(day);
    };

    const weekDates = getWeekDates();
    const getDateForDay = (day) => weekDates[day];
    // All rows on screen share the selected day, so one state gates every control
    const selectedState = dayState(weekDates[selectedDay]);
    const isToday = selectedState === "today";
    const canSkip = selectedState !== "past";

    // The backend route toggles, so undo is the same call
    const handleSkipMeal = async (memberId, period, day, isSkipped = false) => {
        const question = isSkipped
            ? `Undo the skip for ${period} on ${day}?`
            : `Are you sure you want to skip ${period} on ${day} for this user?`;
        if (!window.confirm(question)) return;
        try {
            const date = getDateForDay(day);
            const res = await skipMealSubscribedMember(memberId, { date, period, day });
            if (res.data && res.data.success) {
                toast.success(isSkipped ? 'Skip removed' : 'Meal marked as skipped');
                fetchMembers();
            }
        } catch (err) {
            console.error("Skip err", err);
            toast.error("Failed to skip meal");
        }
    };

    const handleDeliverMeal = async (memberId, period, day, isDelivered) => {
        const question = isDelivered
            ? `Undo delivery for ${period} on ${day}? This adds the meal back to their balance.`
            : `Mark ${period} on ${day} as delivered? This deducts one meal from their balance.`;
        if (!window.confirm(question)) return;
        try {
            const date = getDateForDay(day);
            const res = await deliverMealSubscribedMember(memberId, { date, period, day });
            if (res.data && res.data.success) {
                toast.success('Delivery status updated');
                fetchMembers();
            }
        } catch (err) {
            console.error("Deliver err", err);
            toast.error("Failed to update delivery status");
        }
    };

    // Drafts live only until saved; the saved value comes back on meal.mealGiven
    const mealKey = (meal) => `${meal.memberId}-${meal.period}`;

    const handleMealGivenUpdate = async (meal) => {
        try {
            const date = getDateForDay(meal.day);
            const res = await setMealGivenSubscribedMember(meal.memberId, {
                date,
                period: meal.period,
                day: meal.day,
                meal: mealGivenDrafts[mealKey(meal)] ?? meal.mealGiven
            });
            if (res.data && res.data.success) {
                toast.success("Meal served updated");
                setMealGivenDrafts(prev => {
                    const next = { ...prev };
                    delete next[mealKey(meal)];
                    return next;
                });
                fetchMembers();
            }
        } catch (err) {
            console.error("Meal given err", err);
            toast.error("Failed to update meal served");
        }
    };

    const handleReduceMeals = async () => {
        if (!window.confirm(`Deduct meals for all non-skipped members programmed for ${selectedDay}?`)) return;
        try {
            const date = getDateForDay(selectedDay);
            const res = await reduceDailyMeals({ date, day: selectedDay });
            if (res.data && res.data.success) {
                toast.success(`Reduced meals for ${res.data.count} members.`);
                fetchMembers();
            }
        } catch (err) {
            console.error("Reduce err", err);
            toast.error("Failed to reduce daily meals");
        }
    };

    const numberOfMealsLeft = m => mealsLeft(m.numberOfMeals);
    const isLow = (m) => numberOfMealsLeft(m) <= LOW_MEALS;
    const mealsOver = m => numberOfMealsLeft(m) === 0;

    // Filter and transform data based on selections
    const getFilteredMeals = () => {
        const results = [];
        const targetDateStr = getDateForDay(selectedDay);

        members.forEach(member => {
            if (!member.mealPlans || member.mealPlans.length === 0) return;

            member.mealPlans.forEach(plan => {
                // Check period filter
                if (plan.period?.toLowerCase() !== selectedPeriod.toLowerCase()) return;

                // Check day filter
                let dayPreferences = plan.days?.[selectedDay];

                // Let's resolve what preferences we apply. Wait, if user completely disabled the plan for this day?
                // the calendar modal only allows modifying preferences that overlap an existing expected plan day.
                // So the baseline is if dayPreferences is truthy.
                if (dayPreferences && dayPreferences.length > 0) {
                    let isCustomized = false;

                    // Did the customer override this specific date from their frontend calendar?
                    const customOverride = member.customizedMeals?.find(cm => cm.date === targetDateStr && cm.period === plan.period);
                    if (customOverride) {
                        dayPreferences = customOverride.preferences;
                        isCustomized = true;
                    }

                    if (mealsOver(member)) {
                        return;
                    }

                    results.push({
                        memberId: member._id,
                        name: member.name,
                        mobile: member.mobile,
                        numberOfMeals: member.numberOfMeals,
                        customizationRequest: member.customizationRequest,
                        specialRequest: member.specialRequest,
                        period: plan.period,
                        day: selectedDay,
                        preferences: dayPreferences,
                        isCustomized, // Flag so we can show a badge
                        bowlType: plan.bowlType,
                        proteinQuantity: plan.proteinQuantity,
                        deliveryTime: plan.deliveryTime,
                        skippedMeals: member.skippedMeals || [],
                        deliveredMeals: member.deliveredMeals || [],
                        isSkipped: (member.skippedMeals || []).some(sm => sm.date === targetDateStr && sm.period === plan.period),
                        isDelivered: (member.deliveredMeals || []).some(dm => dm.date === targetDateStr && dm.period === plan.period),
                        mealGiven: (member.mealsGiven || []).find(mg => mg.date === targetDateStr && mg.period === plan.period)?.meal || ""
                    });
                }
            });
        });

        // Done rows sink: pending first (by delivery time), then delivered, then skipped
        const rank = (m) => m.isSkipped ? 2 : m.isDelivered ? 1 : 0;
        results.sort((a, b) =>
            (rank(a) - rank(b)) || (timeToMinutes(a.deliveryTime) - timeToMinutes(b.deliveryTime))
        );

        return results;
    };

    const filteredMeals = getFilteredMeals();

    const getSummary = () => {
        // Skipped meals are not cooked, so they leave every count except their own
        const activeMeals = filteredMeals.filter(m => !m.isSkipped);
        const counts = {
            total: activeMeals.length,
            skipped: filteredMeals.length - activeMeals.length,
            delivered: activeMeals.filter(m => m.isDelivered).length,
            pending: activeMeals.filter(m => !m.isDelivered).length,
            customizationRequest: 0,
            specialRequest: 0,
            diets: Object.fromEntries(DIETS.map(d => [d, { total: 0, bases: {}, bowls: {} }]))
        };

        activeMeals.forEach(meal => {
            if (meal.customizationRequest) counts.customizationRequest++;
            if (meal.specialRequest) counts.specialRequest++;

            DIETS.filter(d => meal.preferences.includes(d)).forEach(diet => {
                const entry = counts.diets[diet];
                entry.total++;
                // ponytail: "Rice bowls" is the legacy label for "Rice"
                BASES.filter(b => meal.preferences.includes(b) || (b === "Rice" && meal.preferences.includes("Rice bowls")))
                    .forEach(b => { entry.bases[b] = (entry.bases[b] || 0) + 1; });
                if (meal.bowlType) entry.bowls[meal.bowlType] = (entry.bowls[meal.bowlType] || 0) + 1;
            });
        });

        return counts;
    };

    const summary = getSummary();

    return (
        <div className="space-y-6">
            <div className="flex sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-lg shadow-sm gap-4">
                <h2 className="text-xl font-semibold text-gray-800">
                    Today's Meal
                </h2>
                <div className="flex flex-wrap gap-4 text-sm mt-4 sm:mt-0">
                    <div className="flex flex-col gap-2 self-start">
                        <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-100 min-w-[150px]">
                            <div className="font-bold text-blue-800 mb-1 border-b border-blue-200 pb-1">Total Orders: {summary.total}</div>
                            <div className="text-blue-700 text-xs flex justify-between"><span>Delivered:</span> <span className="font-medium">{summary.delivered}</span></div>
                            <div className="text-blue-700 text-xs flex justify-between"><span>Pending:</span> <span className="font-medium">{summary.pending}</span></div>
                            <div className="text-red-600 text-xs flex justify-between"><span>Skipped:</span> <span className="font-medium">{summary.skipped}</span></div>
                        </div>
                        <div className="bg-orange-50 px-4 py-2 rounded-lg border border-orange-100 min-w-[150px]">
                            <div className="font-bold text-orange-800 mb-1 border-b border-orange-200 pb-1">Requests</div>
                            <div className="text-orange-700 text-xs flex justify-between"><span>Customization:</span> <span className="font-medium">{summary.customizationRequest}</span></div>
                            <div className="text-orange-700 text-xs flex justify-between"><span>Special:</span> <span className="font-medium">{summary.specialRequest}</span></div>
                        </div>
                    </div>
                    {DIETS.map(diet => {
                        const c = DIET_CARD[diet];
                        const entry = summary.diets[diet];
                        return (
                            <div key={diet} className={`px-4 py-2 rounded-lg border min-w-[150px] ${c.box}`}>
                                <div className={`font-bold mb-1 border-b pb-1 ${c.head}`}>{diet}: {entry.total}</div>
                                {BASES.map(base => (
                                    <div key={base} className={`text-xs flex justify-between ${c.row}`}>
                                        <span>{base}:</span> <span className="font-medium">{entry.bases[base] || 0}</span>
                                    </div>
                                ))}
                                {Object.entries(entry.bowls).map(([bowl, count]) => (
                                    <div key={bowl} className={`flex justify-between capitalize px-1 mt-1 rounded-lg ${c.chip}`}><span>{bowl}:</span> <span className="font-medium">{count}</span></div>
                                ))}
                            </div>
                        );
                    })}
                </div>

            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setSelectedPeriod("Lunch")}
                        className={`px-8 py-2 rounded-lg font-bold text-lg transition-colors border ${selectedPeriod === "Lunch" ? "bg-green-500 text-white border-green-500 shadow-md" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"}`}
                    >
                        Lunch
                    </button>
                    <button
                        onClick={() => setSelectedPeriod("Dinner")}
                        className={`px-8 py-2 rounded-lg font-bold text-lg transition-colors border ${selectedPeriod === "Dinner" ? "bg-green-500 text-white border-green-500 shadow-md" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"}`}
                    >
                        Dinner
                    </button>
                </div>
                <div className="flex flex-wrap gap-3 items-center">
                    {selectedDay !== todayName && (
                        <span className="px-3 py-2 rounded-lg bg-amber-100 text-amber-900 border border-amber-300 text-sm font-bold">
                            You are not at today's meal listing
                        </span>
                    )}
                    {WEEK_DAYS.map(day => (
                        <label
                            key={day}
                            className={`flex items-center space-x-2 cursor-pointer px-3 py-2 rounded-lg border transition ${day === todayName
                                ? "bg-green-100 border-green-400 ring-1 ring-green-400"
                                : "bg-gray-50 border-gray-200 hover:bg-green-50"}`}
                        >
                            <input
                                type="radio"
                                name="mealDay"
                                checked={selectedDay === day}
                                onChange={() => handleDayToggle(day)}
                                className="h-4 w-4 rounded-full border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className={`text-sm font-medium ${day === todayName ? "text-green-800 font-bold" : "text-gray-700"}`}>
                                {day} · {shortDate(weekDates[day])}{day === todayName && " • Today"}
                            </span>
                        </label>
                    ))}
                    <div className="ml-auto flex items-center gap-3 border-l pl-4 border-gray-200">
                        {alarm && (
                            <button
                                onClick={() => setAlarm(null)}
                                className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-bold transition shadow-sm whitespace-nowrap animate-pulse"
                                title={alarm.text}
                            >
                                🔔 Stop Alert — {alarm.text}
                            </button>
                        )}
                        {isToday && (
                            <button
                                onClick={handleReduceMeals}
                                className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition shadow-sm whitespace-nowrap"
                            >
                                Process End of Day
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="p-8 text-center text-gray-500 bg-white rounded-lg shadow-sm">Loading today's meals...</div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MEMBER</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PERIOD & DAY</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PREFERENCES</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SUBSCRIPTION TYPE</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REQUESTS</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredMeals.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                            No meals found for the selected day and period.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredMeals.map((meal, idx) => (
                                        <tr
                                            key={`${meal.memberId}-${meal.period}-${meal.day}-${idx}`}
                                            className={blinkKeys[`${meal.memberId}-${meal.period}-${getDateForDay(meal.day)}`]
                                                ? "transition-colors align-top bg-red-300 animate-pulse"
                                                : meal.isSkipped
                                                    ? "transition-colors align-top bg-red-200 text-red-900"
                                                    : `transition-colors align-top ${meal.isDelivered ? "bg-gray-100 text-gray-500 opacity-75" : ""} ${slotClass(meal.deliveryTime)}`}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-gray-900 text-base">{meal.name}</div>
                                                <div className="text-xs text-gray-500 mt-1">{meal.mobile}</div>
                                                <div className="mt-1">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${isLow(meal) ? "bg-red-600 text-white border-red-700" : "bg-blue-50 text-blue-700 border-blue-100"}`}>
                                                        {isLow(meal) && "⚠️ "}{meal.numberOfMeals}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="font-semibold text-gray-800">{meal.period}</div>
                                                <div className="text-sm text-gray-500">{meal.day}</div>
                                                <div className={`mt-1 inline-block text-xs font-bold px-2 py-1 rounded border bg-green-600 text-green-100`}>
                                                    {meal.deliveryTime || "No time set"}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1 items-center">
                                                    {meal.preferences.map((pref, pIdx) => (
                                                        <span key={pIdx} className={`text-xs px-2 py-1 rounded font-medium ${PREF_COLORS[pref] || "bg-gray-100 text-gray-800"}`}>
                                                            {pref}
                                                        </span>
                                                    ))}
                                                    {meal.isCustomized && (
                                                        <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-bold border border-indigo-200 ml-1">
                                                            User Modified
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-1 text-sm text-gray-700">
                                                    {meal.bowlType
                                                        ? <div className="font-medium capitalize">{meal.bowlType}</div>
                                                        : <span className="text-gray-400 italic">None</span>}
                                                    {meal.bowlType === "Only Protein" && meal.proteinQuantity && (
                                                        <div className="text-lg font-extrabold text-gray-900">Qty: {meal.proteinQuantity}</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {(meal.customizationRequest || meal.specialRequest) ? (
                                                    <div className="space-y-2 text-xs max-w-xs">
                                                        {meal.customizationRequest && (
                                                            <div className="bg-yellow-50 p-2 rounded border border-yellow-100">
                                                                <span className="font-semibold text-yellow-800 block mb-0.5">Customization:</span>
                                                                <span className="text-yellow-700">{meal.customizationRequest}</span>
                                                            </div>
                                                        )}
                                                        {meal.specialRequest && (
                                                            <div className="bg-orange-50 p-2 rounded border border-orange-100">
                                                                <span className="font-semibold text-orange-800 block mb-0.5">Special:</span>
                                                                <span className="text-orange-700">{meal.specialRequest}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 italic text-sm">None</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-2 items-center">
                                                    {meal.isSkipped && (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                                            Skipped
                                                        </span>
                                                    )}
                                                    {canSkip && (meal.isSkipped ? (
                                                        <button
                                                            onClick={() => handleSkipMeal(meal.memberId, meal.period, meal.day, true)}
                                                            className="text-white bg-gray-600 hover:bg-gray-700 px-3 py-1 rounded text-xs transition shadow-sm font-medium"
                                                        >
                                                            Undo Skip
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleSkipMeal(meal.memberId, meal.period, meal.day)}
                                                            className="text-white bg-orange-500 hover:bg-orange-600 px-3 py-1 rounded text-xs transition shadow-sm font-medium"
                                                        >
                                                            Skip
                                                        </button>
                                                    ))}
                                                    {isToday && (
                                                        <label className="flex items-center space-x-1 cursor-pointer ml-3">
                                                            <input
                                                                type="checkbox"
                                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                                checked={meal.isDelivered}
                                                                onChange={() => handleDeliverMeal(meal.memberId, meal.period, meal.day, meal.isDelivered)}
                                                                disabled={meal.isSkipped}
                                                            />
                                                            <span className="text-xs font-medium text-gray-700">Delivered</span>
                                                        </label>
                                                    )}
                                                    {selectedState === "past" && meal.isDelivered && (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                                            Delivered
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mt-2 flex gap-3">
                                                    <button
                                                        onClick={() => {
                                                            setModalTitle(`${meal.name} - Skipped Meals`);
                                                            setModalData(meal.skippedMeals);
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="text-xs font-medium text-gray-500 hover:text-gray-700 underline"
                                                    >
                                                        Skipped Info
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setModalTitle(`${meal.name} - Delivered Meals`);
                                                            setModalData(meal.deliveredMeals);
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="text-xs font-medium text-gray-500 hover:text-gray-700 underline"
                                                    >
                                                        Delivery Info
                                                    </button>
                                                </div>
                                                {isToday && (
                                                    <div className="mt-2 flex gap-2 items-center">
                                                        <input
                                                            type="text"
                                                            value={mealGivenDrafts[mealKey(meal)] ?? meal.mealGiven}
                                                            onChange={(e) => setMealGivenDrafts(prev => ({ ...prev, [mealKey(meal)]: e.target.value }))}
                                                            placeholder="Meal served..."
                                                            className="w-40 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-green-500 focus:border-green-500"
                                                        />
                                                        <button
                                                            onClick={() => handleMealGivenUpdate(meal)}
                                                            className="bg-green-600 text-white hover:bg-green-700 px-3 py-1 rounded text-xs font-medium transition shadow-sm"
                                                        >
                                                            Update
                                                        </button>
                                                    </div>
                                                )}
                                                {selectedState === "past" && meal.mealGiven && (
                                                    <div className="mt-2 text-xs text-gray-600">Served: {meal.mealGiven}</div>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <MealInfoModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={modalTitle}
                data={modalData}
            />
        </div>
    );
};

export default TodaysMealManagement;
